import { AfterViewInit, Component, ElementRef, Injector, OnDestroy, OnInit, QueryList, ViewChildren } from "@angular/core";
import { FeatureName } from "@common/ADAPT.Common.Model/embed/feature-name.enum";
import { ProgressCategory } from "@common/ADAPT.Common.Model/organisation/progress-category";
import { ProgressItem } from "@common/ADAPT.Common.Model/organisation/progress-item";
import { ProgressItemStatusMetadata } from "@common/ADAPT.Common.Model/organisation/progress-item-status";
import { ProgressProduct } from "@common/ADAPT.Common.Model/organisation/progress-product";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { ProgressService } from "@common/progress/progress.service";
import { BaseRoutedComponent } from "@common/ux/base-routed.component";
import { ChartUtils } from "@common/ux/base-ui.service/chart-utils";
import { IDxChartCustomizePoint, IDxChartTooltipPointInfo } from "@common/ux/dx.types";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { JourneyProgressAuthService } from "@org-common/lib/journey-progress/journey-progress-auth.service";
import { OrganisationService } from "@org-common/lib/organisation/organisation.service";
import { OrganisationPageRouteBuilder } from "@org-common/lib/route/organisation-page-route-builder";
import { DoneEvent } from "devextreme/viz/pie_chart";
import { DxPieChartComponent } from "devextreme-angular";
import { filter, lastValueFrom, switchMap } from "rxjs";
import { debounceTime } from "rxjs/operators";

interface IChartData {
    label: string;
    color: string;
    count: number;
    percent: number;
    itemCount: number;
}

interface IGroupedChartData {
    name: string;
    data: IChartData[];
}

@Component({
    selector: "adapt-journey-progress-page",
    standalone: false,
    templateUrl: "./journey-progress-page.component.html",
    styleUrl: "./journey-progress-page.component.scss",
})
export class JourneyProgressPageComponent extends BaseRoutedComponent implements OnInit, OnDestroy, AfterViewInit {
    public progressProducts: ProgressProduct[] = [];
    public categoryChartData: IGroupedChartData[] = [];

    public canEdit = false;

    @ViewChildren(DxPieChartComponent) private pieCharts!: QueryList<DxPieChartComponent>;

    constructor(
        injector: Injector,
        elementRef: ElementRef,
        rxjsBreezeService: RxjsBreezeService,
        authorisationService: AuthorisationService,
        private organisationService: OrganisationService,
        private commonDataService: CommonDataService,
        private progressService: ProgressService,
    ) {
        super(injector, elementRef);

        rxjsBreezeService.breezeEntityChanged$.pipe(
            filter((c) => c.entity instanceof ProgressItem),
            debounceTime(100),
            switchMap(() => this.updateData()),
            this.takeUntilDestroyed(),
        ).subscribe();

        authorisationService.getHasAccess(JourneyProgressAuthService.EditJourneyProgress).pipe(
            this.takeUntilDestroyed(),
        ).subscribe((canEdit) => this.canEdit = canEdit);
    }

    public async ngOnInit() {
        this.commonDataService.disableSaveOverlayForEntityTypes(ProgressItem);

        await this.updateData();

        this.notifyActivated();
    }

    public ngOnDestroy() {
        this.commonDataService.enableSaveOverlayForEntityTypes(ProgressItem);
        super.ngOnDestroy();
    }

    public ngAfterViewInit() {
        // refresh the pie charts after view init, otherwise they don't show up after navigation (when using sizeGroup)
        setTimeout(() => this.pieCharts.forEach((chart) => chart.instance.refresh()));
    }

    public async updateData() {
        this.progressProducts = await lastValueFrom(this.progressService.getProgressProducts(this.organisationService.getOrganisationId()));
        this.progressProducts.forEach((p) => p.progressCategories.sort(SortUtilities.getSortByFieldFunction<ProgressCategory>("ordinal")));

        this.categoryChartData = this.getCategoryChartData();
    }

    public customiseChartPoint(point: IDxChartCustomizePoint) {
        if (point.data.color) {
            return { color: point.data.color };
        } else {
            return {};
        }
    }

    @Autobind
    public customiseChartTooltip(info: IDxChartTooltipPointInfo) {
        const data = info.point.data as IChartData;
        return {
            text: `${data.label}: ${data.count} / ${data.itemCount}`,
        };
    }

    @Autobind
    public customizeLabel(info: IDxChartTooltipPointInfo) {
        const data = info.point.data as IChartData;
        // don't show a percent for To do, misleading when no tasks done but it says 100%
        return data.label !== ProgressItemStatusMetadata.ToDo.name
            ? info.percentText
            : ``;
    }

    public updateChartDimensionAndSubscribeSizeChange(e: DoneEvent) {
        ChartUtils.updateChartDimension(e);
        this.sizeChange$.subscribe(() => ChartUtils.updateChartDimension(e));
    }

    private getCategoryChartData() {
        const groupedChartData: IGroupedChartData[] = [];

        const categories = [{
            name: "Overall",
            items: this.progressProducts.flatMap((p) => p.progressCategories.flatMap((c) => c.progressItems)),
            product: undefined as ProgressProduct | undefined,
        }];

        // if there's only one category, we can show just the overall progress
        if (this.progressProducts.length > 1) {
            categories.push(...this.progressProducts.sort(SortUtilities.getSortByFieldFunction<ProgressProduct>("ordinal"))
                .map((product) => ({
                    name: product.name,
                    items: product.progressCategories.flatMap((c) => c.progressItems),
                    product,
                })));
        }

        for (const category of categories) {
            const itemCount = category.items.length;
            if (itemCount > 0) {
                const data: IChartData[] = ProgressItemStatusMetadata.All.reduce<IChartData[]>((chartData, progressItemStatus) => {
                    const count = category.items.filter((i) => i.status === progressItemStatus.status).length;
                    if (count > 0) {
                        chartData.push({
                            label: progressItemStatus.name,
                            color: progressItemStatus.colour,
                            count,
                            percent: count / itemCount,
                            itemCount,
                        });
                    }
                    return chartData;
                }, []);
                groupedChartData.push({ name: category.name, data });
            }
        }

        return groupedChartData;
    }
}

export const journeyProgressPageRoute = new OrganisationPageRouteBuilder<{ teamId: number }>()
    .usingNgComponent("adapt-journey-progress-page", JourneyProgressPageComponent)
    .atOrganisationUrl("/leadership/progress")
    .withTitle("Journey Progress")
    .verifyingFeatures(FeatureName.Progress)
    .verifyingAccess(JourneyProgressAuthService.ReadJourneyProgress)
    .build();
